import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Grow,
    CircularProgress,
    Box,
} from '@mui/material';

type ConfirmationDialogProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    message: string;
    content?: React.ReactNode;
    onConfirm: () => void;
    confirmButtonLabel?: string;
    cancelButtonLabel?: string;
    confirmButtonSx?: any;
    cancelButtonSx?: any;
    isLoading?: boolean;
    loadingLabel?: string;
};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    onClose,
    title,
    message,
    content,
    onConfirm,
    confirmButtonLabel = 'Confirm',
    cancelButtonLabel = 'Cancel',
    confirmButtonSx,
    cancelButtonSx,
    isLoading = false,
    loadingLabel = 'Saving...',
}) => {
    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            TransitionComponent={Grow}
            TransitionProps={{ timeout: 240 }}
            sx={{ '& .MuiDialog-paper': { borderRadius: 2, width: { xs: '95%', sm: 'auto' } } }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
            <DialogContent dividers sx={{ minHeight: 88 }}>
                <DialogContentText>{message}</DialogContentText>
                {content && (
                    <Box sx={{ mt: 1, maxHeight: 280, overflowY: 'auto' }}>
                        {content}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    color="secondary"
                    variant="outlined"
                    disabled={isLoading}
                    sx={{ textTransform: 'none', fontWeight: 600, ...cancelButtonSx }}
                >
                    {cancelButtonLabel}
                </Button>
                <Button
                    onClick={onConfirm}
                    color="primary"
                    variant="contained"
                    autoFocus
                    disabled={isLoading}
                    sx={{ textTransform: 'none', fontWeight: 600, ...confirmButtonSx }}
                >
                    {isLoading ? (
                        <>
                            <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                            {loadingLabel}
                        </>
                    ) : (
                        confirmButtonLabel
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
